import React, { useState } from "react";
import moment from "moment";
import lodashGet from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
// material ui
import withStyles from "@material-ui/styles/withStyles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
// charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";
// icons
import ExcelIcon from "mdi-material-ui/FileExcel";
import ArrowBack from "@material-ui/icons/ArrowBack";
//jazasoft
import { PageFooter, Button, RestMethods, FETCH_START, FETCH_END } from "jazasoft";
//local
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";

//helpers
import { humanizeNumber, isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { dataProvider } from "../../../App";
import handleError from "../../../utils/handleError";
import { colorSecondary, colorOrange, colorLemom, colorLightGreen, colorTomatoRed, colorPrimary, colorPurple } from "../../../utils/colors";
import { MacroReportId } from "../../../constants";
import { useDeepMemo } from "../../../hooks";
import MuiTable from "../../../components/MuiTable";

const columnsDetail = [
  { dataKey: "date", title: "Date", filterOptions: { filter: true } },
  {
    dataKey: "poRef",
    title: "PO Ref",
    linkPath: record => {
      window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank");
    }
  },
  { dataKey: "customer", title: "Customer", filterOptions: { filter: true } },
  { dataKey: "unitId", title: "Unit", filterOptions: { filter: true } },
  { dataKey: "product", title: "Product", filterOptions: { filter: true } },
  { dataKey: "style", title: "Style" },
  { dataKey: "color", title: "Color" },
  {
    dataKey: "part",
    title: "Part",
    render: (value, _, row) => `${value}${!isEmpty(row.placement) ? ` - ${row.placement}` : ""}`,
    filterOptions: { filter: true }
  },
  { dataKey: "lot", title: "Lot", render: (value, _, row) => `${row.lotType === "Bulk" ? "Lot" : row.lotType}${value}` },
  { dataKey: "cutQty", title: "Cut Qty" },
  { dataKey: "csv", title: "CSV", filterOptions: { filter: true } },
  { dataKey: "netIssued", title: "Net Issued" },
  { dataKey: "wastage", title: "Wastage" },
  { dataKey: "wastagePercent", title: "Wastage (%)" }
];

const getEchartOptions = aggregateWastageReport => {
  if (!aggregateWastageReport || Object.keys(aggregateWastageReport).length === 0) return {};

  return {
    color: [colorLightGreen.main, colorOrange.main, colorLemom.main, colorSecondary.main, colorPrimary.light, colorTomatoRed.main, colorPurple.main],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow"
      }
    },
    legend: {
      x: "center",
      y: "bottom",
      data: ["usedInPanel", "endLoss", "endBits", "damaged", "blanketQty", "missing", "unused"]
    },
    grid: {
      left: "5%",
      right: "4%",
      top: "5%",
      containLabel: true
    },
    xAxis: [
      {
        type: "category",
        data: Object.keys(aggregateWastageReport)
      }
    ],
    yAxis: [
      {
        type: "value",
        axisLabel: {
          formatter: "{value}%"
        }
      }
    ],
    series: [
      {
        name: "usedInPanel",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].usedInPanel)
      },
      {
        name: "endLoss",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].endLoss)
      },
      {
        name: "endBits",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].endBits)
      },
      {
        name: "damaged",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].damaged)
      },
      {
        name: "blanketQty",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].blanketQty)
      },
      {
        name: "missing",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].missing)
      },
      {
        name: "unused",
        type: "bar",
        stack: "wastage",
        barWidth: 25,
        data: Object.keys(aggregateWastageReport).map(month => aggregateWastageReport[month].unused),
        label: {
          show: true,
          color: "black",
          fontSize: 10,
          position: "top",
          formatter: param => {
            const month = Object.keys(aggregateWastageReport)[param.dataIndex];
            const record = aggregateWastageReport[month] || {};
            return `${record.wastagePercent}%`;
          }
        }
      }
    ]
  };
};

const styles = {
  root: {
    margin: "1.5em 0"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  }
};

const WastageReportView = props => {
  const { classes, filter = {}, history } = props;
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [month, setMonth] = useState(null);
  const i18nMessages = useSelector(state => state.i18n?.messages || {});

  const reportList = props.orderList.map(e => ({
    ...e,
    month: moment(e.orderCloseDate).format("MMM, YY")
  }));

  const onDownload = () => {
    const fileName = "WASTAGE.xlsx";
    const orders = reportList.filter(o => {
      let result = true;
      if (filter.start && filter.end) {
        result = o.orderCloseDate >= moment(filter.start).startOf("day").valueOf() && o.orderCloseDate <= moment(filter.end).endOf("day").valueOf();
      }
      if (result && filter.customerList?.length > 0) {
        result = filter.customerList.map(({ name }) => name).includes(o.customer);
      }
      if (result && filter.season) {
        result = filter.season === o.season;
      }
      if (result && filter.line) {
        result = filter.line === o.line;
      }
      if (result && filter.productList?.length > 0) {
        result = filter.productList.map(({ name }) => name).includes(o.product);
      }
      if (result && filter.supplierList?.length > 0) {
        result = filter.supplierList.map(({ name }) => name).includes(o.supplier);
      }
      if (result && filter.fabricPatternList?.length > 0) {
        result = filter.fabricPatternList.map(({ name }) => name).includes(o.fabricPattern);
      }
      if (result && filter.fabricCategoryList?.length > 0) {
        result = filter.fabricCategoryList.map(({ name }) => name).includes(o.fabricCategory);
      }
      return result;
    });

    const columns = [
      { dataKey: "unitId", title: "Unit" },
      { dataKey: "orderCloseDate", title: "Close Date", type: "date" },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "customer", title: "Buyer" },
      { dataKey: "season", title: "Season" },
      { dataKey: "product", title: "Product" },
      { dataKey: "style", title: "Style" },
      { dataKey: "styleNo", title: "Style No" },
      { dataKey: "color", title: "Style Color" },
      { dataKey: "part", title: "Part" },
      { dataKey: "placement", title: "Placement" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "cutQty", title: "Cut Qty" },
      { dataKey: "cutQtyPercent", title: "Cut Qty (%)", cellStyle: { dataFormat: "0.0%" } },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "netIssued", title: "Net Issued Fabric" },
      { dataKey: "avgRollLength", title: "Average Roll Length" },
      { dataKey: "splitCount", title: "No of Splitting" },
      { dataKey: "endLoss", title: "End Loss" },
      { dataKey: "endBits", title: "Net End Bits" },
      { dataKey: "damaged", title: "Damaged" },
      { dataKey: "usedInPanel", title: "Used In Panel Replacement" },
      { dataKey: "blanketQty", title: "Testing Loss" },
      { dataKey: "missing", title: "Missed in Cutting" },
      { dataKey: "unused", title: "Unused in Cutting" },
      { dataKey: "wastage", title: "Total Wastage" },
      { dataKey: "wastagePercent", title: "Total Wastage(%)", cellStyle: { dataFormat: "0.00%" } },
      { dataKey: "supplier", title: "Supplier" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));

    const rowData = orders
      .map(r => {
        const netIssued = r.issued - r.returned;
        const wastage = r.endLoss + r.endBits + r.usedInPanel + r.damaged + r.blanketQty + r.unused + r.missing;
        const avgRollLength = Math.round(netIssued / (r.rollCount - r.splitCount));
        const cutQtyPercent = r.orderQty > 0 ? toFixed(r.cutQty / r.orderQty, 3) : 0.0;
        return {
          ...r,
          lot: `${r.lotType === "Bulk" ? "Lot" : r.lotType}${r.lot}`,
          netIssued,
          avgRollLength,
          wastage,
          wastagePercent: toFixed(wastage / netIssued, 4),
          cutQtyPercent
        };
      })
      .map((row, idx) => ({ rowNo: idx + 1, cellList: columns.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] })) }));

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [
        {
          sheetNo: 0,
          sheetName: "WASTAGE REPORT",
          autoSizeColumn: false,
          excelData: { columnList: columns, rowList: rowData }
        }
      ],
      params: { filename: fileName }
    };

    dispatch({ type: FETCH_START });
    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          const filename = header ? header.match(/filename="(.+)"/)[1] : "WASTAGE.xlsx";

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
        dispatch({ type: FETCH_END });
      })
      .catch(err => {
        handleError(err, dispatch);
        dispatch({ type: FETCH_END });
      });
  };

  const onBack = () => {
    if (page === 2) {
      setPage(1);
      setMonth(null);
    } else {
      history.goBack();
    }
  };

  const columnsSummary = useDeepMemo(() => {
    return [
      {
        dataKey: "month",
        title: "Month",
        linkPath: record => {
          setPage(2);
          setMonth(record.month);
        }
      },
      { dataKey: "poCount", title: "Cut Orders", align: "center" },
      { dataKey: "cutQty", title: "Cut Qty", render: value => humanizeNumber(value), align: "right" },
      { dataKey: "netIssued", title: "Net Issued", render: value => humanizeNumber(toFixedValue(value, 0)), align: "right" },
      { dataKey: "wastage", title: "Wastage", align: "right" },
      { dataKey: "wastagePercent", title: "Wastage (%)", render: value => `${value}%`, align: "right" }
    ];
  }, [setPage, setMonth]);

  const { rows, options } = useDeepMemo(() => {
    let rows = [],
      options = {};
    if (page === 1) {
      let months = [];
      let curr = moment(filter.end);
      while (curr.valueOf() >= moment(filter.start).startOf("month").valueOf()) {
        months.unshift(curr.format("MMM, YY"));
        curr = curr.subtract(1, "months");
      }

      let aggregateWastageReport = {}; // month -> {[dataKey]: value}, where dataKey = month, noOfPos, ...
      months.forEach(month => {
        const monthReportList = reportList.filter(e => e.month === month);
        const cutQty = monthReportList.reduce((acc, el) => acc + el.cutQty, 0);
        const netIssued = monthReportList.reduce((acc, el) => acc + (el.issued - el.returned), 0);
        const wastage = monthReportList.reduce(
          (acc, el) => acc + el.endBits + el.endLoss + el.usedInPanel + el.damaged + el.unused + el.missing + el.blanketQty,
          0
        );
        const endLossTotal = monthReportList.reduce((acc, el) => acc + el.endLoss, 0);
        const endBitsTotal = monthReportList.reduce((acc, el) => acc + el.endBits, 0);
        const usedInPanelTotal = monthReportList.reduce((acc, el) => acc + el.usedInPanel, 0);
        const damagedTotal = monthReportList.reduce((acc, el) => acc + el.damaged, 0);
        const unusedTotal = monthReportList.reduce((acc, el) => acc + el.unused, 0);
        const missingTotal = monthReportList.reduce((acc, el) => acc + el.missing, 0);
        const blanketTotal = monthReportList.reduce((acc, el) => acc + el.blanketQty, 0);

        const wastagePercent = netIssued > 0 ? (wastage * 100) / netIssued : 0;
        const endLoss = netIssued > 0 ? (endLossTotal * 100) / netIssued : 0;
        const endBits = netIssued > 0 ? (endBitsTotal * 100) / netIssued : 0;
        const damaged = netIssued > 0 ? (damagedTotal * 100) / netIssued : 0;
        const usedInPanel = netIssued > 0 ? (usedInPanelTotal * 100) / netIssued : 0;
        const unused = netIssued > 0 ? (unusedTotal * 100) / netIssued : 0;
        const missing = netIssued > 0 ? (missingTotal * 100) / netIssued : 0;
        const blanketQty = netIssued > 0 ? (blanketTotal * 100) / netIssued : 0;

        aggregateWastageReport[month] = {
          month,
          poCount: monthReportList.length,
          cutQty,
          netIssued: toFixed(netIssued, 1),
          wastage: toFixed(wastage, 1),
          wastagePercent: toFixed(wastagePercent, 2),
          endLoss: toFixed(endLoss, 2),
          endBits: toFixed(endBits, 2),
          damaged: toFixed(damaged, 2),
          usedInPanel: toFixed(usedInPanel, 2),
          unused: toFixed(unused, 2),
          missing: toFixed(missing, 2),
          blanketQty: toFixed(blanketQty, 2)
        };
      });

      rows = [...months].reverse().map(month => aggregateWastageReport[month]);
      options = getEchartOptions(aggregateWastageReport);
    } else if (page === 2) {
      rows = reportList
        .filter(e => e.month === month)
        .sort((a, b) => b.orderCloseDate - a.orderCloseDate)
        .map(order => ({
          ...order,
          ...order,
          date: moment(order.orderCloseDate).format("ll"),
          csv: `${order.csv === true ? "Yes" : "No"}`,
          netIssued: toFixed(order.issued - order.returned, 2),
          wastage: toFixed(order.endBits + order.endLoss + order.usedInPanel + order.damaged + order.blanketQty + order.missing + order.unused, 2),
          wastagePercent: `${toFixed(
            ((order.endBits + order.endLoss + order.usedInPanel + order.damaged + order.blanketQty + order.missing + order.unused) * 100) /
              (order.issued - order.returned),
            2
          )}%`
        }));
    }
    return { rows, options };
  }, [page, filter, reportList, month]);

  if (reportList.length === 0) {
    return (
      <div style={{ marginTop: "3em" }}>
        <Typography className={classes.displayCenter} color="primary" variant="subtitle1">
          Data not available
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {page === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardHeader title="Monthly Wastage (%)" />
                <CardContent>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={options}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardHeader title="Monthly Wastage" />
                <Divider />
                <CardContent style={{ padding: 0, height: 288, overflowY: "auto" }}>
                  <MuiTable columns={columnsSummary} rows={rows} i18nKey={`resources.report.wastage`} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {page === 2 && (
          <MuiTable
            title={`Monthly Wastage: ${month}`}
            columns={columnsDetail}
            rows={rows}
            i18nKey={`resources.report.wastage`}
            pageable
            searchable
            searchKeys={["poRef", "style"]}
          />
        )}
      </div>

      <PageFooter className={classes.displayCenter}>
        <Button label="Back" variant="contained" onClick={onBack}>
          <ArrowBack />
        </Button>
        <Button label="Download" variant="contained" style={{ color: "green", marginLeft: "1em" }} onClick={onDownload}>
          <ExcelIcon />
        </Button>
      </PageFooter>
    </div>
  );
};

const StyledWastageReport = withStyles(styles)(WastageReportView);

const WastageReport = props => (
  <Report title="Wastage Report" defaultMonths={6} macroReportId={MacroReportId.WASTAGE} {...props}>
    <StyledWastageReport {...props} />
  </Report>
);

export default WastageReport;
